import { logError } from '@ardoq/logging';
import { yfiles, Insets, LayoutExecutorAsync, TimeSpan } from '@ardoq/yfiles';
import { RunLayoutArgs } from './types';
import type { ViewIds } from '@ardoq/api-types';
import { isWorkerError } from './layoutWorkerErrorReporting';
import { createLayoutExecutorRegistry } from './layoutExecutorRegistry';
import { runMainThreadLayout } from './runMainThreadLayout';
import { getLayoutWorkerTarget } from './layoutWorker';

let licensedWorker: Worker | null = null;
const ensureLicense = (worker: Worker) => {
  if (licensedWorker === worker) {
    return;
  }
  const license = (yfiles as unknown & { license?: unknown }).license;
  if (!license) {
    logError(Error('yFiles license not found.'));
    return;
  }
  worker.postMessage(license);
  licensedWorker = worker;
};
let transaction = 0;

const isFailureOfTransaction = (data: unknown, layoutTransaction: number) =>
  isWorkerError(data) && data.extraData?.transaction === layoutTransaction;

const postLayoutToWorker = (
  worker: Worker,
  viewId: ViewIds,
  data: object
): Promise<object> => {
  const currentTransaction = transaction++;
  return new Promise((resolve, reject) => {
    const handler = (event: MessageEvent<any>) => {
      if (isFailureOfTransaction(event.data, currentTransaction)) {
        worker.removeEventListener('message', handler);
        reject(Error('Layout calculation failed in the layout worker'));
        return;
      }
      if (
        isWorkerError(event.data) ||
        event.data.transaction !== currentTransaction
      ) {
        return;
      }
      resolve(event.data);
      worker.removeEventListener('message', handler);
    };
    worker.addEventListener('message', handler);
    ensureLicense(worker);
    worker.postMessage({ ...data, viewId, transaction: currentTransaction });
  });
};

const activeLayoutExecutors =
  createLayoutExecutorRegistry<LayoutExecutorAsync>();
const runWebWorkerLayout = async ({
  graphComponent,
  layoutDescriptor,
  layoutData,
  duration = TimeSpan.fromMilliseconds(0),
  animateViewport = false,
  updateContentRect = false,
  targetBoundsInsets = Insets.EMPTY,
  portAdjustmentPolicy,
  viewId,
  configureTableLayout,
  viewInstanceId,
}: RunLayoutArgs) => {
  const shouldAnimate = duration.totalMilliseconds > 0;
  const executor = new LayoutExecutorAsync({
    messageHandler: data =>
      getLayoutWorkerTarget(viewId).then(target => {
        if (target.kind === 'worker') {
          return postLayoutToWorker(target.worker, viewId, data);
        }
        if (
          viewInstanceId &&
          !activeLayoutExecutors.isActive(viewInstanceId, executor)
        ) {
          return data;
        }
        return runMainThreadLayout(viewId, data);
      }),
    graphComponent,
    layoutDescriptor,
    layoutData,
    animationDuration: duration,
    easedAnimation: shouldAnimate,
    animateViewport,
    updateContentBounds: updateContentRect,
    targetBoundsPadding: targetBoundsInsets,
    portAdjustmentPolicies: portAdjustmentPolicy,
    configureTableLayout,
  });
  if (viewInstanceId) {
    activeLayoutExecutors.activate(viewInstanceId, executor);
  }

  try {
    await executor.start();
  } finally {
    if (viewInstanceId) {
      activeLayoutExecutors.release(viewInstanceId, executor);
    }
  }
};

export { runWebWorkerLayout };
